<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, toRaw } from 'vue';
import { closeModuleWindow, fromSandbox, moduleHasKeyboard, moduleWindows, onWindowPost, sandboxDoc, sendWindowMessage, type ModuleWindow } from '../modules';
import { readBridgeMessage, SANDBOX_FED, vSandbox } from '../editor/sandbox';
import { t } from '../i18n';
import Icon from './Icon.vue';

/**
 * Windows of modules (API 2.3): floating windows over the platform, like the diagram code window.
 * The module's document runs on the sandbox page; the title bar moves the window, the corner resizes
 * it (when allowed), Esc or × closes it. A modal window dims the rest until it is closed.
 * API 4.6: the window's document keeps the keyboard only when the module has `keyboard` (the title
 * bar says so); Escape inside it gives the keyboard back to the window, a second Escape closes it.
 */
const frames = new Map<string, HTMLIFrameElement>();
/** Windows whose document is there: messages go straight in, the others wait. */
const ready = new Set<string>();
const top = ref<string | null>(null);

const modal = computed(() => moduleWindows.some((w) => w.modal));

function bindFrame(w: ModuleWindow, el: unknown): void {
  const f = el as HTMLIFrameElement | null;
  if (!f) {
    frames.delete(w.handle);
    ready.delete(w.handle);
    return;
  }
  if (frames.get(w.handle) === f) return;
  frames.set(w.handle, f);
  f.addEventListener(SANDBOX_FED, () => {
    // the sandbox page has the document: what was posted meanwhile goes in now, in order
    setTimeout(() => {
      ready.add(w.handle);
      // the queue lives in reactive state: post the plain data (a proxy cannot be cloned)
      for (const data of toRaw(w.queue).splice(0)) f.contentWindow?.postMessage({ vpView: 1, data: toRaw(data) }, '*');
    }, 0);
  });
}

let unsub: (() => void) | null = null;
function onMessage(e: MessageEvent): void {
  for (const [handle, f] of frames) {
    if (e.source !== f.contentWindow) continue;
    const b = readBridgeMessage(e.data);
    if (b) {
      if (b.close) closeModuleWindow(handle);
      else if (b.escape) (f.closest('.module-window') as HTMLElement | null)?.focus();
      return;
    }
    const d = e.data as { vpView?: number; data?: unknown } | null;
    const w = moduleWindows.find((x) => x.handle === handle);
    if (d && d.vpView === 1 && w && fromSandbox(w.moduleId, d.data)) sendWindowMessage(handle, d.data);
    return;
  }
}
/** The window whose document has the keyboard now; one of a module without `keyboard` never keeps it. */
const keyFocus = ref<string | null>(null);
function checkFocus(): void {
  const a = document.activeElement;
  let handle: string | null = null;
  for (const [h, f] of frames) if (f === a) handle = h;
  const w = handle ? moduleWindows.find((x) => x.handle === handle) : undefined;
  if (w && !moduleHasKeyboard(w.moduleId)) {
    keyFocus.value = null;
    (frames.get(w.handle)?.closest('.module-window') as HTMLElement | null)?.focus();
    return;
  }
  keyFocus.value = w?.handle ?? null;
}
const onBlur = () => setTimeout(checkFocus, 0);

onMounted(() => {
  window.addEventListener('message', onMessage);
  window.addEventListener('blur', onBlur);
  window.addEventListener('focus', checkFocus);
  unsub = onWindowPost((handle, data) => {
    const w = moduleWindows.find((x) => x.handle === handle);
    if (!w) return;
    if (ready.has(handle)) frames.get(handle)?.contentWindow?.postMessage({ vpView: 1, data }, '*');
    else w.queue.push(data);
  });
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  window.removeEventListener('blur', onBlur);
  window.removeEventListener('focus', checkFocus);
  unsub?.();
});

function place(w: ModuleWindow): Record<string, string> {
  const W = Math.min(w.width, window.innerWidth - 24);
  const H = Math.min(w.height + 44, window.innerHeight - 24);
  const x = w.x ?? Math.max(12, (window.innerWidth - W) / 2 + w.offset);
  const y = w.y ?? Math.max(12, (window.innerHeight - H) / 2 + w.offset);
  return { left: x + 'px', top: y + 'px', width: W + 'px', height: H + 'px', zIndex: String(top.value === w.handle ? 1960 : 1950) };
}

/** Drag by the title bar; the iframe ignores the pointer meanwhile so the drag does not stop over it. */
const dragging = ref<string | null>(null);
function startDrag(w: ModuleWindow, e: PointerEvent): void {
  if ((e.target as HTMLElement).closest('button')) return;
  const box = (e.currentTarget as HTMLElement).parentElement!.getBoundingClientRect();
  const dx = e.clientX - box.left;
  const dy = e.clientY - box.top;
  dragging.value = w.handle;
  top.value = w.handle;
  const move = (ev: PointerEvent) => {
    w.x = Math.min(Math.max(ev.clientX - dx, 8 - box.width + 80), window.innerWidth - 80);
    w.y = Math.min(Math.max(ev.clientY - dy, 8), window.innerHeight - 40);
  };
  const up = () => {
    dragging.value = null;
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

/** Size follows the CSS resize handle; it is remembered so a re-render keeps it. */
function onResize(w: ModuleWindow, e: Event): void {
  const el = e.currentTarget as HTMLElement;
  w.width = Math.round(el.offsetWidth);
  w.height = Math.round(el.offsetHeight - 44);
}

function onKey(w: ModuleWindow, e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.stopPropagation();
    closeModuleWindow(w.handle);
  }
}
</script>

<template>
  <div v-if="modal" class="window-backdrop" aria-hidden="true"></div>
  <section
    v-for="w in moduleWindows"
    :key="w.handle"
    class="module-window float card"
    :class="{ resizable: w.resizable, dragging: dragging === w.handle }"
    role="dialog"
    :aria-modal="w.modal"
    :aria-label="w.title"
    :data-window="w.window ?? ''"
    :data-handle="w.handle"
    :style="place(w)"
    tabindex="-1"
    @pointerdown="top = w.handle"
    @keydown="onKey(w, $event)"
    @mouseup="w.resizable && onResize(w, $event)"
  >
    <header @pointerdown="startDrag(w, $event)">
      <Icon name="module" :size="14" />
      <strong class="title">{{ w.title }}</strong>
      <span v-if="keyFocus === w.handle" class="key-hint" data-key-badge>{{ t('modules.keyboardHere') }}</span>
      <button class="icon-btn" :aria-label="t('common.close')" :title="t('common.close')" data-act="close-window" @click="closeModuleWindow(w.handle)"><Icon name="close" :size="15" /></button>
    </header>
    <iframe :ref="(el) => bindFrame(w, el)" :data-module="w.moduleId" v-sandbox="sandboxDoc(w.moduleId, w.html)" :title="w.title" :style="{ pointerEvents: dragging ? 'none' : 'auto' }"></iframe>
  </section>
</template>

<style scoped>
.window-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1940;
  background: color-mix(in srgb, var(--backdrop) 55%, transparent);
}
.module-window {
  position: fixed;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  border-radius: 16px;
  min-width: 200px;
  min-height: 140px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 16px);
}
.module-window.resizable {
  resize: both;
}
header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 6px 0 14px;
  border-bottom: 1px solid var(--border);
  cursor: grab;
  user-select: none;
  color: var(--muted);
}
.dragging header {
  cursor: grabbing;
}
.key-hint {
  flex: none;
  font-size: 12px;
  color: var(--muted);
}
.title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
  font-size: 13.5px;
}
iframe {
  flex: 1;
  width: 100%;
  border: none;
  background: transparent;
  color-scheme: normal;
}
</style>
